import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalContent, ModalHeader, ModalBody } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtySpinner, ConvertToLocalDate, ConvertToLocalDateOnly } from '@circuitry-ai/doc-ui';
import type { Schedule } from 'modules/shared/data/src/features/settings/settingstypes';
import { getScheduleTypeClass, getScheduleStateClass, scheduleTypeOptions } from '../scheduler-utils';
import { useGetScheduleDetailsQuery } from '@circuitry-ai/doc-data';

interface ScheduleDetailsViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: string | null;
}

const DAYS_OF_WEEK = {
  'SUN': 'Sunday',
  'MON': 'Monday',
  'TUE': 'Tuesday',
  'WED': 'Wednesday',
  'THU': 'Thursday',
  'FRI': 'Friday',
  'SAT': 'Saturday'
};


const extractScheduleFromResponse = (scheduleDetails: unknown): Schedule | undefined => {
  if (!scheduleDetails) return undefined;
  
  if ((scheduleDetails as any).scheduler_id) {
    return scheduleDetails as Schedule;
  }
  
  const scheduleDetailsRecord = scheduleDetails as unknown as Record<string, unknown>;
  if (!scheduleDetailsRecord.data || typeof scheduleDetailsRecord.data !== 'object') {
    return undefined;
  }
  
  const data = scheduleDetailsRecord.data as Record<string, unknown>;
  if ((data as any).scheduler_id) {
    return data as unknown as Schedule;
  }
  if (Array.isArray(data.items) && data.items.length > 0) {
    return data.items[0] as unknown as Schedule;
  }
  if (Array.isArray(data) && data.length > 0) {
    return data[0] as unknown as Schedule;
  }
  return undefined;
};

const getPatternTypeDisplay = (patternType: string, t: (key: string) => string): string => {
  if (patternType === 'DAILY') return t('Daily');
  if (patternType === 'WEEKLY') return t('Weekly');
  if (patternType === 'MONTHLY') return t('Monthly');
  return patternType;
};

const getScheduleTypeDisplay = (simplifiedType: string | undefined, t: (key: string) => string): string => {
  if (simplifiedType === 'Rate' || simplifiedType === scheduleTypeOptions.RATE) {
    return t('Rate');
  }
  if (simplifiedType === 'One-time' || simplifiedType === scheduleTypeOptions.One_time) {
    return t('One-time');
  }
  return t('Recurring');
};

export function ScheduleDetailsViewModal({ isOpen, onClose, scheduleId }: Readonly<ScheduleDetailsViewModalProps>) {
  const { t } = useTranslation();
  const { data: scheduleDetails, isLoading, isFetching } = useGetScheduleDetailsQuery(scheduleId || '', { skip: !scheduleId || !isOpen });
  const schedule = React.useMemo(() => extractScheduleFromResponse(scheduleDetails), [scheduleDetails]);

  if (!isOpen) return null;

  const scheduleAny = schedule as any;
  const expressionMetadata = scheduleAny?.expression_metadata;
  const pattern = expressionMetadata?.pattern;
  const simplifiedType = scheduleAny?.simplified_type || schedule?.schedule_type;
  const simplifiedExpression = scheduleAny?.simplified_expression;

  // Rate can come from expression_metadata.schedule_rate or expression_metadata.pattern (type RATE, value, unit)
  const rateConfig = expressionMetadata?.schedule_rate ?? (pattern?.type === 'RATE' && pattern?.value != null ? { value: pattern.value, unit: pattern.unit ?? 'minutes' } : null);
  const isRateSchedule = simplifiedType === 'Rate' || simplifiedType === scheduleTypeOptions.RATE;

  // Full date + time (e.g. for Created Date)
  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    try {
      return ConvertToLocalDate(dateStr);
    } catch {
      return dateStr;
    }
  };

  // Date only, no time (e.g. for Schedule Configuration)
  const formatDateOnly = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    try {
      return ConvertToLocalDateOnly(dateStr);
    } catch {
      return dateStr;
    }
  };

  const convertHour24To12 = (hour24: number): number => {
    if (hour24 === 0) return 12;
    if (hour24 > 12) return hour24 - 12;
    return hour24;
  };

  // Format time from 24-hour to 12-hour for display
  const formatTime = (time24h: string | undefined): string => {
    if (!time24h) return '-';
    const [hours, minutes] = time24h.split(':');
    if (!hours || !minutes) return time24h;
    const hour24 = Number.parseInt(hours, 10);
    if (Number.isNaN(hour24)) return time24h;
    const hour12 = convertHour24To12(hour24);
    const ampm = hour24 >= 12 ? 'pm' : 'am';
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Get frequency display
  const getFrequencyDisplay = (): string => {
    if (pattern?.type) {
      return getPatternTypeDisplay(pattern.type, t);
    }
    return getScheduleTypeDisplay(simplifiedType, t);
  };

  // Get end condition display
  const getEndConditionDisplay = (): string => {
    // Check if end_date exists and is not null
    if (scheduleAny?.end_date !== null && scheduleAny?.end_date !== undefined) {
      return `${t('Ends on')} - ${formatDateOnly(scheduleAny.end_date)}`;
    }
    return t('Never ends');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent className="max-h-[90vh]">
        <ModalHeader className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-foreground">{t('Schedule Details')}</h2>
          </ModalHeader>
        <ModalBody className="overflow-y-auto">
          {isLoading||isFetching || !schedule ? (
            <div className="flex items-center justify-center py-8">
              <CtySpinner size="md" />
              <span className="ml-2 text-default-500">{t('Loading schedule details...')}</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* BASIC INFORMATION */}
              <div className="border-b border-default-200 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-default-600 uppercase">{t('Basic Information')}</h3>
                  <Icon icon="mdi:pencil" className="w-4 h-4 text-default-400" />
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Schedule Name')}</label>
                    <p className="text-base text-foreground">{schedule.schedule_name}</p>
                  </div>
                  {schedule.description && (
                    <div>
                      <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Description')}</label>
                      <p className="text-sm text-default-600">{schedule.description}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Type')}</label>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getScheduleTypeClass(simplifiedType)}`}>
                        {getScheduleTypeDisplay(simplifiedType, t)}
                      </span>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('State')}</label>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getScheduleStateClass(schedule.state)}`}>
                        {schedule.state === 'ENABLED' ? t('Enabled') : t('Disabled')}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Schedule Expression')}</label>
                    <input
                      type="text"
                      value={simplifiedExpression || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-default-300 rounded-md bg-default-100 text-sm text-default-600"
                    />
                  </div>
                  {schedule.created_at && (
                    <div>
                      <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Created Date')}</label>
                      <p className="text-sm text-default-600">{formatDate(schedule.created_at)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* TARGET CONFIGURATION */}
              <div className="border-b border-default-200 pb-4">
                <div className="flex items-center gap-2 mb-4">
                  <Icon icon="mdi:target" className="w-5 h-5 text-default-500" />
                  <h3 className="text-sm font-bold text-default-600 uppercase">{t('Target Configuration')}</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">
                      {schedule.target_type === 'agent' ? t('Agents') : t('Targets')} (1)
                    </label>
                    <div className="flex items-center gap-2 p-3 bg-default-100 rounded-md border border-default-200">
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-semibold">
                        {schedule.target_type === 'agent' ? t('Agent') : schedule.target_type}
                      </span>
                      <span className="text-sm text-default-600">{schedule.target_id}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* SCHEDULE CONFIGURATION */}
              {(simplifiedType === 'Recurring' || simplifiedType === scheduleTypeOptions.Recurring) && pattern && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon icon="mdi:cog" className="w-5 h-5 text-default-500" />
                    <h3 className="text-sm font-bold text-default-600 uppercase">{t('Schedule Configuration')}</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon icon="mdi:calendar-clock" className="w-4 h-4 text-default-400" />
                        <label className="text-xs font-semibold text-default-500 uppercase">{t('Frequency')}</label>
                      </div>
                      <p className="text-sm text-default-600 ml-6">{getFrequencyDisplay()}</p>
                    </div>
                    
                    {(pattern.type === 'DAILY' || pattern.type === 'MONTHLY') && pattern.repeat_every && (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon icon="mdi:information-outline" className="w-4 h-4 text-default-400" />
                          <label className="text-xs font-semibold text-default-500 uppercase">{t('Interval')}</label>
                        </div>
                        <p className="text-sm text-default-600 ml-6">
                          {t('Every')} {pattern.repeat_every} {pattern.type === 'DAILY' ? t('day(s)') : t('month(s)')}
                        </p>
                      </div>
                    )}

                    {pattern.type === 'WEEKLY' && pattern.days && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Icon icon="mdi:calendar" className="w-4 h-4 text-default-400" />
                          <label className="text-xs font-semibold text-default-500 uppercase">{t('Days of Week')}</label>
                        </div>
                        <div className="ml-6 flex flex-wrap gap-2">
                          {pattern.days.map((day: string) => (
                            <span
                              key={day}
                              className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium"
                            >
                              {t(DAYS_OF_WEEK[day as keyof typeof DAYS_OF_WEEK] || day)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {scheduleAny?.start_date && (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon icon="mdi:calendar" className="w-4 h-4 text-default-400" />
                          <label className="text-xs font-semibold text-default-500 uppercase">{t('Start Date')}</label>
                        </div>
                        <p className="text-sm text-default-600 ml-6">{formatDateOnly(scheduleAny.start_date)}</p>
                      </div>
                    )}

                    {pattern.time && (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon icon="mdi:clock-outline" className="w-4 h-4 text-default-400" />
                          <label className="text-xs font-semibold text-default-500 uppercase">{t('Start Time')}</label>
                        </div>
                        <p className="text-sm text-default-600 ml-6">{formatTime(pattern.time)}</p>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon icon="mdi:earth" className="w-4 h-4 text-default-400" />
                        <label className="text-xs font-semibold text-default-500 uppercase">{t('Timezone')}</label>
                      </div>
                      <p className="text-sm text-default-600 ml-6">{schedule.schedule_timezone || schedule.timezone || 'UTC'}</p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon icon="mdi:information-outline" className="w-4 h-4 text-default-400" />
                        <label className="text-xs font-semibold text-default-500 uppercase">{t('End Condition')}</label>
                      </div>
                      <p className="text-sm text-default-600 ml-6">{getEndConditionDisplay()}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Rate Schedule Configuration */}
              {isRateSchedule && rateConfig && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon icon="mdi:cog" className="w-5 h-5 text-default-500" />
                    <h3 className="text-sm font-bold text-default-600 uppercase">{t('Schedule Configuration')}</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Rate')}</label>
                      <p className="text-sm text-default-600">
                        {t('Every')} {rateConfig.value} {rateConfig.unit}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Timezone')}</label>
                      <p className="text-sm text-default-600">{schedule.schedule_timezone || schedule.timezone || 'UTC'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* One-time Schedule Configuration */}
              {(simplifiedType === 'One-time' || simplifiedType === scheduleTypeOptions.One_time) && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon icon="mdi:cog" className="w-5 h-5 text-default-500" />
                    <h3 className="text-sm font-bold text-default-600 uppercase">{t('Schedule Configuration')}</h3>
                  </div>
                  <div className="space-y-4">
                    {expressionMetadata?.schedule_at && (
                      <>
                        <div>
                          <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Date')}</label>
                          <p className="text-sm text-default-600">{formatDateOnly(expressionMetadata.schedule_at.split('T')[0])}</p>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Time')}</label>
                          <p className="text-sm text-default-600">{formatTime(expressionMetadata.schedule_at.split('T')[1]?.slice(0, 5))}</p>
                        </div>
                      </>
                    )}
                    <div>
                      <label className="text-xs font-semibold text-default-500 uppercase mb-1 block">{t('Timezone')}</label>
                      <p className="text-sm text-default-600">{schedule.schedule_timezone || schedule.timezone || 'UTC'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
